<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AutomationAPI from 'dashboard/api/automation';

const props = defineProps({
  automationId: {
    type: [Number, String],
    default: null,
  },
  publishedVersionId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const hasError = ref(false);
const diff = ref(null);
let activeRequestId = 0;

const PRIMARY_SECTIONS = ['event_name', 'conditions', 'actions'];

const sectionFromPath = path => path.toString().split('.')[0].split('[')[0];

function sectionLabel(section) {
  switch (section) {
    case 'event_name':
      return t('AUTOMATION.DIFF.SECTION.EVENT');
    case 'conditions':
      return t('AUTOMATION.DIFF.SECTION.CONDITIONS');
    case 'actions':
      return t('AUTOMATION.DIFF.SECTION.ACTIONS');
    case 'priority':
      return t('AUTOMATION.DIFF.SECTION.PRIORITY');
    case 'failure_policy':
      return t('AUTOMATION.DIFF.SECTION.FAILURE_POLICY');
    case 'metadata':
      return t('AUTOMATION.DIFF.SECTION.METADATA');
    default:
      return t('AUTOMATION.DIFF.SECTION.OTHER');
  }
}

function changeTypeLabel(type) {
  switch (type) {
    case 'added':
      return t('AUTOMATION.DIFF.TYPE.ADDED');
    case 'removed':
      return t('AUTOMATION.DIFF.TYPE.REMOVED');
    case 'changed':
    default:
      return t('AUTOMATION.DIFF.TYPE.CHANGED');
  }
}

const summary = computed(() => diff.value?.summary || {});
const changes = computed(() => diff.value?.changes || []);
const sectionCounts = computed(() => summary.value.by_section || {});
const typeCounts = computed(() => summary.value.by_type || {});
const totalChanges = computed(
  () => summary.value.total_changes ?? changes.value.length
);
const hasChanges = computed(() => changes.value.length > 0);
const hasPublishedVersion = computed(() => Boolean(props.publishedVersionId));

const primarySectionSummaries = computed(() =>
  PRIMARY_SECTIONS.map(section => ({
    key: section,
    label: sectionLabel(section),
    count: sectionCounts.value[section] || 0,
  }))
);

const changeTypeSummaries = computed(() =>
  Object.entries(typeCounts.value).map(([type, count]) => ({
    type,
    label: changeTypeLabel(type),
    count,
  }))
);

const versionLabel = computed(() => {
  if (!diff.value) return '';

  const baseId = diff.value.base_version_id;
  const targetId = diff.value.target_version_id;
  if (!baseId) {
    return t('AUTOMATION.DIFF.VERSION_WITHOUT_BASE', { target: targetId });
  }

  return t('AUTOMATION.DIFF.VERSION_RANGE', { base: baseId, target: targetId });
});

function changeTypeClass(type) {
  const classMap = {
    added: 'bg-n-teal-3 text-n-teal-11',
    removed: 'bg-n-ruby-3 text-n-ruby-11',
    changed: 'bg-n-amber-3 text-n-amber-11',
  };
  return classMap[type] || 'bg-n-slate-3 text-n-slate-11';
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return t('AUTOMATION.DIFF.EMPTY_VALUE');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

async function loadDiff() {
  const automationId = props.automationId;
  const requestId = activeRequestId + 1;
  activeRequestId = requestId;

  diff.value = null;
  hasError.value = false;

  if (!automationId) return;

  isLoading.value = true;

  try {
    const response = await AutomationAPI.diff(automationId);
    if (requestId === activeRequestId) {
      diff.value = response.data.payload;
    }
  } catch {
    if (requestId === activeRequestId) {
      hasError.value = true;
    }
  } finally {
    if (requestId === activeRequestId) {
      isLoading.value = false;
    }
  }
}

watch(() => props.automationId, loadDiff, { immediate: true });
</script>

<template>
  <section
    class="mb-5 rounded-lg border border-n-weak bg-n-background p-3"
    data-test="automation-version-diff-panel"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <h3 class="mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('AUTOMATION.DIFF.TITLE') }}
        </h3>
        <p class="m-0 text-xs text-n-slate-11">
          {{ $t('AUTOMATION.DIFF.SUBTITLE') }}
        </p>
      </div>
      <span
        v-if="diff"
        class="shrink-0 rounded-md bg-n-slate-3 px-2 py-1 text-xs font-medium text-n-slate-11"
        data-test="automation-version-diff-total"
      >
        {{ $t('AUTOMATION.DIFF.TOTAL', { count: totalChanges }) }}
      </span>
    </div>

    <div v-if="isLoading" class="mt-3 text-sm text-n-slate-11">
      {{ $t('AUTOMATION.DIFF.LOADING') }}
    </div>

    <div
      v-else-if="hasError"
      class="mt-3 rounded-md bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
      data-test="automation-version-diff-error"
    >
      {{ $t('AUTOMATION.DIFF.ERROR') }}
    </div>

    <div v-else-if="diff" class="mt-3 grid gap-3">
      <div class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11">
        <span data-test="automation-version-diff-range">
          {{ versionLabel }}
        </span>
        <span
          v-if="!hasPublishedVersion"
          class="rounded-md bg-n-amber-3 px-2 py-1 font-medium text-n-amber-11"
          data-test="automation-version-diff-unpublished"
        >
          {{ $t('AUTOMATION.DIFF.UNPUBLISHED') }}
        </span>
      </div>

      <div class="grid gap-2 sm:grid-cols-3">
        <div
          v-for="section in primarySectionSummaries"
          :key="section.key"
          class="rounded-lg border border-n-weak px-3 py-2"
          :data-test="`automation-version-diff-summary-${section.key}`"
        >
          <div class="text-xs text-n-slate-11">
            {{ section.label }}
          </div>
          <div class="text-base font-medium text-n-slate-12">
            {{ section.count }}
          </div>
        </div>
      </div>

      <div
        v-if="changeTypeSummaries.length"
        class="flex flex-wrap gap-2"
        data-test="automation-version-diff-types"
      >
        <span
          v-for="item in changeTypeSummaries"
          :key="item.type"
          class="rounded-md bg-n-slate-3 px-2 py-1 text-xs font-medium text-n-slate-11"
        >
          {{ item.label }} {{ item.count }}
        </span>
      </div>

      <div
        v-if="!hasChanges"
        class="rounded-md bg-n-slate-2 px-3 py-2 text-sm text-n-slate-11"
        data-test="automation-version-diff-empty"
      >
        {{ $t('AUTOMATION.DIFF.EMPTY') }}
      </div>

      <ul v-else class="grid list-none gap-2 p-0 m-0">
        <li
          v-for="change in changes"
          :key="`${change.path}-${change.change_type}`"
          class="rounded-lg border border-n-weak p-3"
          data-test="automation-version-diff-change"
        >
          <div class="mb-2 flex flex-wrap items-center gap-2">
            <span
              class="rounded-md px-2 py-1 text-xs font-medium"
              :class="changeTypeClass(change.change_type)"
            >
              {{ changeTypeLabel(change.change_type) }}
            </span>
            <span class="text-xs font-medium text-n-slate-12">
              {{ sectionLabel(sectionFromPath(change.path)) }}
            </span>
            <code class="text-xs text-n-slate-11 break-all">
              {{ change.path }}
            </code>
          </div>
          <div class="grid gap-2 sm:grid-cols-2">
            <div>
              <div class="mb-1 text-xs font-medium text-n-slate-11">
                {{ $t('AUTOMATION.DIFF.BEFORE') }}
              </div>
              <div
                class="m-0 min-h-9 whitespace-pre-wrap break-words rounded-md bg-n-slate-2 p-2 text-xs text-n-slate-12"
              >
                {{ formatValue(change.before) }}
              </div>
            </div>
            <div>
              <div class="mb-1 text-xs font-medium text-n-slate-11">
                {{ $t('AUTOMATION.DIFF.AFTER') }}
              </div>
              <div
                class="m-0 min-h-9 whitespace-pre-wrap break-words rounded-md bg-n-slate-2 p-2 text-xs text-n-slate-12"
              >
                {{ formatValue(change.after) }}
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>
